import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { User, ExternalLink } from 'lucide-react';

function initials(name) {
  const p = (name || '').trim().split(/\s+/);
  if (!p[0]) return '?';
  return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase();
}

export function assigneeList(task) {
  if (task?.assignees?.length) return task.assignees;
  if (task?.assignee_name) {
    return [{ user_id: task.assignee_user_id, name: task.assignee_name, email: task.assignee_email || '', initials: task.assignee_initials || initials(task.assignee_name) }];
  }
  return [];
}

// Assignee chip(s): stacked avatars for multiple assignees, popover with everyone's name/email.
export function AssigneeAvatar({ task, size = 22, showName = false, onOpen }) {
  const navigate = useNavigate();
  const list = assigneeList(task);
  const contactId = task?.assignee_contact_id;

  if (!list.length) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="assignee-unassigned">
        <User size={12} />Unassigned
      </span>
    );
  }

  const shown = list.slice(0, 3);
  const extra = list.length - shown.length;
  const label = list.length === 1 ? list[0].name : `${list[0].name} +${list.length - 1}`;

  return (
    <Popover onOpenChange={o => { if (o) onOpen?.(); }}>
      <PopoverTrigger asChild>
        <button
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 group"
          data-testid="assignee-avatar"
          title={list.map(a => a.name).join(', ')}
        >
          <span className="inline-flex items-center">
            {shown.map((a, i) => (
              <span key={a.user_id || i}
                className="rounded-full flex items-center justify-center font-bold text-white shrink-0 transition-transform group-hover:scale-105"
                style={{ width: size, height: size, fontSize: size * 0.42, background: i === 0 ? '#6994c6' : '#5a7d52',
                         marginLeft: i === 0 ? 0 : -(size * 0.3), border: '1.5px solid #fff', zIndex: 3 - i, position: 'relative' }}>
                {a.initials || initials(a.name)}
              </span>
            ))}
            {extra > 0 && (
              <span className="rounded-full flex items-center justify-center font-bold shrink-0"
                style={{ width: size, height: size, fontSize: size * 0.38, marginLeft: -(size * 0.3), border: '1.5px solid #fff',
                         background: '#e7e3da', color: '#6b7482', position: 'relative' }} data-testid="assignee-extra-count">
                +{extra}
              </span>
            )}
          </span>
          {showName && <span className="text-xs text-muted-foreground truncate max-w-[140px]">{label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 bg-white border-border p-3" onClick={e => e.stopPropagation()}>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{list.length === 1 ? 'Assignee' : `Assignees (${list.length})`}</p>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {list.map((a, i) => (
            <div key={a.user_id || i} className="flex items-center gap-2.5" data-testid={`assignee-popover-row-${a.user_id}`}>
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: i === 0 ? '#6994c6' : '#5a7d52' }}>{a.initials || initials(a.name)}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" data-testid="assignee-popover-name">{a.name}</p>
                {a.email && <p className="text-xs text-muted-foreground truncate">{a.email}</p>}
              </div>
            </div>
          ))}
        </div>
        {contactId && (
          <button
            onClick={() => navigate(`/contacts/${contactId}`)}
            data-testid="assignee-view-contact"
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-lg transition-colors hover:bg-secondary"
            style={{ color: '#6994c6' }}
          >
            <ExternalLink size={12} />View contact
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
